import { FacilityLeadTime, FacilityLeadTimeFormValues } from './../models/IFacility';
import { makeAutoObservable } from 'mobx';
import agent from '../agents';
import { getWeekdays } from '../common/util/common';
import { IFacility, FacilityOpenDates } from '../models';
import { BusinessValidationWarningTypes, RepairTypes } from '../models/constants';
import IApiError from '../models/IApiError';
import FacilityDetails, {
    FacilityOpenDatesFormValues,
    FacilityRepairCapacityFormValues,
    FacilityRepairCapacity,
    CapacityConfigEntryForAWeekday,
    FacilityWaitlist,
    FacilityWaitlistFormValues,
    FacilityCloseDate,
    FacilityCloseDateFormValues,
    FacilityDropoffTimeSetting,
    FacilityDropoffTimeSettingFormValues,
    FacilityCapacityMetricForAWeek as FacilityCapacityMetricForAWeek,
    FacilityRepairSeasonSummaryData,
    FacilityCapacityCopyRequest,
} from '../models/IFacility';
import { WaitlistStatics } from '../models/IWaitlist';

export default class FacilityStore {
    apiOperationError: string = '';
    hasApiOperationCompleted: boolean = false;
    loadingInitial: boolean = false;
    facilities: IFacility[] | undefined = undefined;
    facility: IFacility | undefined = undefined;
    formValues: FacilityDetails = {
        id: '',
        halReference: 0,
        name: '',
        contactDetails: '',
        streetAddressLine1: '',
        streetAddressLine2: '',
        suburb: '',
        state: '',
        postcode: '',
        repairPeriodStartDate: undefined,
        repairPeriodEndDate: undefined,
    };
    facilityRepairCapacity: FacilityRepairCapacity = {
        facilityId: '',
        configEntriesForTheWeek: [],
    };
    facilityRepairCapacityFormValues: FacilityRepairCapacityFormValues = new FacilityRepairCapacityFormValues();
    facilityOpenDatesFormValues: FacilityOpenDatesFormValues = new FacilityOpenDatesFormValues();

    facilityWaitlist: FacilityWaitlist | undefined = undefined;
    facilityWaitlistFormValues: FacilityWaitlistFormValues = new FacilityWaitlistFormValues();

    facilityCloseDate: FacilityCloseDate | undefined = undefined;
    facilityCloseDates: FacilityCloseDate[] | undefined = undefined;
    facilityCloseDateFormValues: FacilityCloseDateFormValues = new FacilityCloseDateFormValues();

    facilityDropoffTimeSetting: FacilityDropoffTimeSetting | undefined = undefined;
    facilityDropoffTimeSettingFormValues: FacilityDropoffTimeSettingFormValues =
        new FacilityDropoffTimeSettingFormValues();

    waitlistStats: WaitlistStatics | undefined = undefined;

    totalCapacityForTheWeek = { PDR: 0, PTP: 0, CON: 0 };
    facilityCapacityMetrics: FacilityCapacityMetricForAWeek[] = [];
    facilityCapacityMetricsOfAWeek: FacilityCapacityMetricForAWeek | undefined = undefined;

    facilityRepairSeasonSummaryData: FacilityRepairSeasonSummaryData | undefined = undefined;

    facilityLeadTimeFormValues: FacilityLeadTimeFormValues = new FacilityLeadTimeFormValues();
    facilityLeadTime: FacilityLeadTime | undefined = undefined;

    constructor() {
        makeAutoObservable(this);
    }
    clearFacility = () => {
        this.setFacility(undefined);
        this.setFormValues(undefined);
    };

    setList = (facilities: IFacility[]) => {
        this.facilities = facilities;
    };

    setFacility = (facility: IFacility | undefined) => {
        this.facility = facility;
    };

    setFormValues = (facility: FacilityDetails | undefined) => {
        if (facility) {
            this.formValues.id = facility.id !== null ? facility.id : '';
            this.formValues.halReference = facility.halReference !== null ? facility.halReference : 0;
            this.formValues.name = facility.name !== null ? facility.name : '';
            this.formValues.contactDetails = facility.contactDetails !== null ? facility.contactDetails : '';
            this.formValues.streetAddressLine1 = facility.streetAddressLine1 !== null ? facility.streetAddressLine1 : '';
            this.formValues.streetAddressLine2 = facility.streetAddressLine2 !== null ? facility.streetAddressLine2 : '';
            this.formValues.suburb = facility.suburb !== null ? facility.suburb : '';
            this.formValues.state = facility.state !== null ? facility.state : '';
            this.formValues.postcode = facility.postcode !== null ? facility.postcode : '';
        } 
        else {
            this.formValues.id = '';
            this.formValues.halReference = 0;
            this.formValues.name = '';
            this.formValues.contactDetails = '';
            this.formValues.streetAddressLine1 = '';
            this.formValues.streetAddressLine2 = '';
            this.formValues.suburb = '';
            this.formValues.state = '';
            this.formValues.postcode = '';
        }
    };
    setFacilityRepairCapacityFormValues = (facilityRepairCapacity: FacilityRepairCapacity) => {
        this.facilityRepairCapacityFormValues = new FacilityRepairCapacityFormValues(facilityRepairCapacity);
    };
    setFacilityRepairCapacity = (facilityRepairCapacity: FacilityRepairCapacity) => {
        this.facilityRepairCapacity = facilityRepairCapacity;
    };
    setFacilityCapacityWeekDay = async (configEntryForTheWeek: CapacityConfigEntryForAWeekday) => {
        const fcwdayIndex = this.facilityRepairCapacityFormValues.configEntriesForTheWeek?.findIndex(
            (e) => e.weekDay === configEntryForTheWeek.weekDay
        );
        if (fcwdayIndex >= 0) {
            this.facilityRepairCapacityFormValues.configEntriesForTheWeek[fcwdayIndex] = configEntryForTheWeek;
        } else {
            this.facilityRepairCapacityFormValues.configEntriesForTheWeek.push(configEntryForTheWeek);
        }

        //calculate totals for each repair type
        this.calculateCapacityTotalsForTheWeek();

        //debugger
        //console.log(this.totalCapacityForTheWeek)
    };
    resetFacilityRepairCapacityFormValues = () => {
        this.setFacilityRepairCapacityFormValues(this.facilityRepairCapacity);
    };
    setFacilityOpenDatesFormValues = (facilityOpenDates: FacilityOpenDates | undefined) => {
        this.facilityOpenDatesFormValues = new FacilityOpenDatesFormValues(facilityOpenDates);
    };

    setOperationError = (error: string) => {
        this.apiOperationError = error;
    };

    setOperationCompleted = (hasCompleted: boolean) => {
        this.hasApiOperationCompleted = hasCompleted;
    };
    setLoadingInitial = (state: boolean) => {
        this.loadingInitial = state;
    };

    setFacilityWaitlistFormValuesFromWaitlist = (facilityWaitlist: FacilityWaitlist | undefined) => {
        this.facilityWaitlistFormValues = new FacilityWaitlistFormValues(facilityWaitlist);
    };

    setFacilityWaitlistFormValues = (facilityWaitlistFormValues: FacilityWaitlistFormValues) => {
        this.facilityWaitlistFormValues = facilityWaitlistFormValues;
    };

    setFacilityWaitlist = (facilityWaitlist: FacilityWaitlist) => {
        this.facilityWaitlist = facilityWaitlist;
    };

    setFacilityCloseDateFormValues = (facilityCloseDate: FacilityCloseDate | undefined) => {
        this.facilityCloseDateFormValues = new FacilityCloseDateFormValues(facilityCloseDate);
    };

    setFacilityCloseDate = (facilityCloseDate: FacilityCloseDate) => {
        this.facilityCloseDate = facilityCloseDate;
    };

    setFacilityCloseDates = (facilityCloseDates: FacilityCloseDate[]) => {
        this.facilityCloseDates = facilityCloseDates;
    };

    setFacilityDropoffTimeSettingFormValues = (facilityDropoffTimeSetting: FacilityDropoffTimeSetting | undefined) => {
        this.facilityDropoffTimeSettingFormValues = new FacilityDropoffTimeSettingFormValues(
            facilityDropoffTimeSetting
        );
    };

    setFacilityDropoffTimeSetting = (facilityDropoffTimeSetting: FacilityDropoffTimeSetting) => {
        this.facilityDropoffTimeSetting = facilityDropoffTimeSetting;
    };

    setFacilityCapacityMetrics = (capacityMetrics: FacilityCapacityMetricForAWeek[]) => {
        this.facilityCapacityMetrics = capacityMetrics;
    };

    setFacilityCapacityMetricsOfAWeek = (capacityMetricsOfAWeek: FacilityCapacityMetricForAWeek) => {
        //there should be one and only one element in metrics array holding metrics for the week
        this.facilityCapacityMetricsOfAWeek = capacityMetricsOfAWeek;
    };

    setFacilityRepairSeasonSummaryData = (summaryData: FacilityRepairSeasonSummaryData) => {
        this.facilityRepairSeasonSummaryData = summaryData;
    };

    setFacilityLeadTimeFormValues = (
        facilityLeadTime: FacilityLeadTime | undefined
    ) => {
        this.facilityLeadTimeFormValues = new FacilityLeadTimeFormValues(facilityLeadTime);
    };

    setFacilityLeadTime = (facilityLeadTime: FacilityLeadTime) => {
        this.facilityLeadTime = facilityLeadTime;
    };

    getFacilityLeadTime = async (facilityId: string) => {
        try {
            //make api class to get data
            this.setOperationError('');
            this.setOperationCompleted(false);
            this.setLoadingInitial(true);

            const facilityLeadTime = await agent.Facility.getFacilityLeadTime(facilityId);
                      
            this.setFacilityLeadTimeFormValues(facilityLeadTime);

        } catch (error) {
            console.log(error);

            //initialise form with empty values when a drop off setting doesn't exist for the facility (api will return 404 error)
            const emptyFacilityLeadTime: FacilityLeadTime = {
                facilityId: facilityId,
                pdrLeadTimeInDays: 0,
                ptpLeadTimeInDays: 0,
                conLeadTimeInDays: 0, 
            };
            this.setFacilityLeadTimeFormValues(emptyFacilityLeadTime);
        } finally {
            this.setOperationCompleted(true);
            this.setLoadingInitial(false);
        }
    };

    saveFacilityLeadTimeFormValues = async (facilityLeadTime: FacilityLeadTime) => {
        try {
            this.setOperationError('');
            this.setOperationCompleted(false);
            //debugger;
            await agent.Facility.saveFacilityLeadTime(facilityLeadTime);

            this.setFacilityLeadTime(facilityLeadTime);
        } catch (error) {
            let apiError: IApiError = error as IApiError;

            if (apiError.businessValidationMessage != '') this.setOperationError(apiError.businessValidationMessage);
            else this.setOperationError('Oops! something went wrong. Please try again.');
        } finally {
            this.setOperationCompleted(true);
        }
    };

    getList = async () => {
        try {
            //make api class to get data
            this.setOperationError('');
            this.setOperationCompleted(false);
            this.setLoadingInitial(true);

            const facilities = await agent.Facility.getList();

            this.setList(facilities);

            this.clearFacility(); //remove any existing facility selected state
        } catch (error) {
            this.setOperationError('Oops! something went wrong. Please try again.');
            console.log(error);
        } finally {
            this.setOperationCompleted(true);
            this.setLoadingInitial(false);
        }
    };

    getFacility = async (facilityId: string) => {
        try {
            //make api class to get data
            this.setOperationError('');
            this.setOperationCompleted(false);
            if (this.facility && this.facility.id === facilityId) {
                // no need to fetch again just return;
            } else {
                this.setLoadingInitial(true);

                const facilityLatest = await agent.Facility.getFacility(facilityId);

                this.setFacility(facilityLatest);
                this.setFormValues(facilityLatest);
                //call set method
                console.log(`fetching faclility for id ${facilityId} ...`);                
            }
        } catch (error) {
            this.setOperationError('Oops! something went wrong. Please try again.');
            console.log(error);
        } finally {
            this.setOperationCompleted(true);
            this.setLoadingInitial(false);
        }
    };

    getFacilityOpenDates = async (facilityId: string) => {
        try {
            //make api class to get data
            this.setOperationError('');
            this.setOperationCompleted(false);
            this.setLoadingInitial(true);

            const facilityOpenDates = await agent.Facility.getFacilityOpenDates(facilityId);

            this.setFacilityOpenDatesFormValues(facilityOpenDates);
        } catch (error) {
            // if dates aren't set it may throw an error
            const emptyFacilityOpenDates: FacilityOpenDates = {
                facilityId: facilityId,
                openFromDate: null,
                shutDownDate: null,
            };
            this.setFacilityOpenDatesFormValues(emptyFacilityOpenDates);
            //this.setOperationError('Oops! something went wrong. Please try again.');
            console.log(error);
        } finally {
            this.setOperationCompleted(true);
            this.setLoadingInitial(false);
        }
    };
    saveFacilityOpenDates = async (
        facilityOpenDates: FacilityOpenDates,
        allowExistingBookingsToFallOutsideRepairSeason: boolean = false
    ) => {
        try {
            this.setOperationError('');
            this.setOperationCompleted(false);

            facilityOpenDates.checkForExistingBookings = !allowExistingBookingsToFallOutsideRepairSeason;

            await agent.Facility.saveFacilityOpenDates(facilityOpenDates);

            //update repair season dates of facility object in the state
            if (this.facility) {
                this.facility.repairPeriodStartDate = facilityOpenDates.openFromDate as Date;
                this.facility.repairPeriodEndDate = facilityOpenDates.shutDownDate as Date;
            }

            this.setFacilityOpenDatesFormValues(facilityOpenDates);
        } catch (error: IApiError | any) {
            console.log(`validation error: ${error?.businessValidationMessage}`);

            if (error?.businessValidationMessage) {
                this.setOperationError(error.businessValidationMessage);
                //re-throw error
                throw new Error(error.businessValidationMessage, {
                    cause: BusinessValidationWarningTypes.BookingsFallOutsideRepairSeason,
                });
            } else {
                this.setOperationError('Oops! something went wrong. Please try again.');
                //re-throw error
                throw new Error('Oops! something went wrong. Please try again.');
            }
        } finally {
            this.setOperationCompleted(true);
        }
    };
    getFacilityRepairCapacity = async (facilityId: string, startDateOfWeek?: Date) => {
        try {
            //make api class to get data
            this.setOperationError('');
            this.setOperationCompleted(false);
            this.setLoadingInitial(true);

            let facilityRepairCapacity = await agent.Facility.getFacilityRepairCapacity(facilityId, startDateOfWeek);
            facilityRepairCapacity.appliesToWeekStarting = startDateOfWeek;

            if (!facilityRepairCapacity) {
                facilityRepairCapacity = {
                    facilityId: facilityId,
                    appliesToWeekStarting: startDateOfWeek,
                    configEntriesForTheWeek: [],
                };
            }
            // Find missing weekdays and set empty repair capacity
            getWeekdays().map((item: string) => {
                const fcwday = facilityRepairCapacity.configEntriesForTheWeek?.find((e) => e.weekDay === item);
                if (!fcwday) {
                    facilityRepairCapacity.configEntriesForTheWeek.push({
                        weekDay: item,
                        capacityItems: Object.keys(RepairTypes).map((item: string) => {
                            return { repairType: item, capacity: 0 };
                        }),
                    });
                }
            });
            this.setFacilityRepairCapacity(facilityRepairCapacity);
            this.setFacilityRepairCapacityFormValues(facilityRepairCapacity);

            //calculate totals
            this.calculateCapacityTotalsForTheWeek();
        } catch (error) {
            this.setOperationError('Oops! something went wrong. Please try again.');
            console.log(error);
        } finally {
            this.setOperationCompleted(true);
            this.setLoadingInitial(false);
        }
    };
    saveFacilityRepairCapacity = async (allowCapacityToGoLowerThanBookings: boolean = false) => {
        try {
            this.setOperationError('');
            this.setOperationCompleted(false);
            //debugger;
            await agent.Facility.saveFacilityRepairCapacity({
                ...this.facilityRepairCapacityFormValues,
                checkCapacityAgainstExistingBookings: !allowCapacityToGoLowerThanBookings,
            });

            this.setFacilityRepairCapacity({
                facilityId: this.facilityRepairCapacityFormValues.facilityId,
                appliesToWeekStarting: this.facilityRepairCapacityFormValues.appliesToWeekStarting,
                configEntriesForTheWeek: [...this.facilityRepairCapacityFormValues.configEntriesForTheWeek],
            });
        } catch (error: IApiError | any) {
            console.log(`validation error: ${error?.businessValidationMessage}`);

            if (error?.businessValidationMessage) {
                this.setOperationError(error.businessValidationMessage);
                //re-throw error
                throw new Error(error.businessValidationMessage, {
                    cause: BusinessValidationWarningTypes.CapacityLowerThanBookings,
                });
            } else {
                this.setOperationError('Oops! something went wrong. Please try again.');
                //re-throw error
                throw new Error('Oops! something went wrong. Please try again.');
            }
        } finally {
            this.setOperationCompleted(true);
        }
    };

    saveFacilityDetails = async (facilityDetails: FacilityDetails) => {
        this.setOperationError('');
        this.setOperationCompleted(false);

        try {
            //debugger;
            await agent.Facility.saveFacilityDetails(facilityDetails);
            this.setFacility(facilityDetails);
            this.setFormValues(facilityDetails);
        } catch (error) {
            this.setOperationError('Oops! something went wrong. Please try again.');
        } finally {
            this.setOperationCompleted(true);
        }
    };

    createFacility = async (facilityDetails: FacilityDetails) => {
        this.setOperationError('');
        this.setOperationCompleted(false);

        try {
            const newFacility = await agent.Facility.createFacility(facilityDetails);
            this.setFacility(newFacility);
            this.setFormValues(newFacility);
            return newFacility;
        } catch (error) {
            this.setOperationError('Oops! something went wrong. Please try again.');
        } finally {
            this.setOperationCompleted(true);
        }
    };

    getFacilityWaitlist = async (facilityId: string) => {
        try {
            //make api class to get data
            this.setOperationError('');
            this.setOperationCompleted(false);
            this.setLoadingInitial(true);

            const facilityWaitlist = await agent.Facility.getFacilityWaitlist(facilityId);

            this.setFacilityWaitlist(facilityWaitlist);
            this.setFacilityWaitlistFormValuesFromWaitlist(facilityWaitlist);

            console.log(this.facilityWaitlist);
            console.log(this.facilityWaitlistFormValues);

            //return facilityWaitlist;
        } catch (error) {
            const emptyFacilityWaitlist: FacilityWaitlist = {
                facilityId: facilityId,
                enable: null,
                pdrSendWaitlistOffers: null,
                ptpSendWaitlistOffers: null,
                conSendWaitlistOffers: null,
                pdrRatio: null,
                ptpRatio: null,
                conRatio: null,
                waitlistEnabledOn: null,
                waitlistEndDate: null,
                waitlistlastRunDate: null,
                waitlistnextRunDate: null,
            };
            this.setFacilityWaitlist(emptyFacilityWaitlist);
            this.setFacilityWaitlistFormValuesFromWaitlist(emptyFacilityWaitlist);
            console.log(error);
        } finally {
            this.setOperationCompleted(true);
            this.setLoadingInitial(false);
        }
    };

    saveFacilityWaitlist = async (updatedFacilityWaitlistData: FacilityWaitlistFormValues) => {
        try {
            this.setOperationError('');
            this.setOperationCompleted(false);

            await agent.Facility.saveFacilityWaitlist(updatedFacilityWaitlistData);
            if (this.facilityWaitlist) {
                let updatedFacilityWaitlist: FacilityWaitlist = {
                    ...this.facilityWaitlist,
                    enable: updatedFacilityWaitlistData.enable,
                    pdrSendWaitlistOffers: updatedFacilityWaitlistData.pdrSendWaitlistOffers,
                    ptpSendWaitlistOffers: updatedFacilityWaitlistData.ptpSendWaitlistOffers,
                    conSendWaitlistOffers: updatedFacilityWaitlistData.conSendWaitlistOffers,
                    pdrRatio: updatedFacilityWaitlistData.pdrRatio,
                    ptpRatio: updatedFacilityWaitlistData.ptpRatio,
                    conRatio: updatedFacilityWaitlistData.conRatio,
                    waitlistEndDate: updatedFacilityWaitlistData.waitlistEndDate
                        ? new Date(updatedFacilityWaitlistData.waitlistEndDate)
                        : null,
                };
                this.setFacilityWaitlist(updatedFacilityWaitlist);
            }
            this.setFacilityWaitlistFormValues(updatedFacilityWaitlistData);
        } catch (error) {
            this.setOperationError('Oops! something went wrong. Please try again.');
        } finally {
            this.setOperationCompleted(true);
        }
    };

    getFacilityCloseDates = async (facilityId: string): Promise<FacilityCloseDate[] | undefined> => {
        try {
            //make api class to get data
            this.setOperationError('');
            this.setOperationCompleted(false);
            this.setLoadingInitial(true);
            const facilityCloseDates = await agent.Facility.getFacilityCloseDates(facilityId);
            this.setFacilityCloseDates(facilityCloseDates);
            return facilityCloseDates;
        } catch (error) {
            console.log(error);
        } finally {
            this.setOperationCompleted(true);
            this.setLoadingInitial(false);
        }
    };

    saveFacilityCloseDate = async (facilityCloseDate: FacilityCloseDate) => {
        try {
            this.setOperationError('');
            this.setOperationCompleted(false);

            facilityCloseDate.checkForExistingBookings = !facilityCloseDate.allowClosureOnTheDayBookingsExist;

            //debugger;

            await agent.Facility.saveFacilityCloseDate(facilityCloseDate);

            this.setFacilityCloseDate(facilityCloseDate);

            //nice to have: once a closed date has been saved, clear the selected date in the form to prepare for another add
            //for some reason this isn't working?
            // const emptyFacilityCloseDate: FacilityCloseDate = {
            //     facilityId: facilityCloseDate.facilityId,
            //     closureDate: null
            // };
            // this.setFacilityCloseDateFormValues(emptyFacilityCloseDate);
        } catch (error: IApiError | any) {
            console.log(`validation error: ${error?.businessValidationMessage}`);

            if (error?.businessValidationMessage) {
                this.setOperationError(error.businessValidationMessage);
                //re-throw error
                throw new Error(error.businessValidationMessage, {
                    cause: BusinessValidationWarningTypes.BookingsExistOnClosedDay,
                });
            } else {
                this.setOperationError('Oops! something went wrong. Please try again.');
                //re-throw error
                throw new Error('Oops! something went wrong. Please try again.');
            }
        } finally {
            this.setOperationCompleted(true);
        }
    };

    deleteFacilityCloseDate = async (facilityCloseDate: FacilityCloseDate) => {
        try {
            this.setOperationError('');
            this.setOperationCompleted(false);

            await agent.Facility.deleteFacilityCloseDate(facilityCloseDate);
        } catch (error) {
            this.setOperationError('Oops! something went wrong. Please try again.');
        } finally {
            this.setOperationCompleted(true);
        }
    };

    getFacilityDropoffTimeSetting = async (facilityId: string) => {
        try {
            //make api class to get data
            this.setOperationError('');
            this.setOperationCompleted(false);
            this.setLoadingInitial(true);
            const facilityDropoffTimeSetting = await agent.Facility.getFacilityDropoffTimeAllowanceSetting(facilityId);
            this.setFacilityDropoffTimeSetting(facilityDropoffTimeSetting);
            this.setFacilityDropoffTimeSettingFormValues(facilityDropoffTimeSetting);
            return facilityDropoffTimeSetting;
        } catch (error) {
            console.log(error);

            //initialise form with empty values when a drop off setting doesn't exist for the facility (api will return 404 error)
            const emptyFacilityDropoffTimeSetting: FacilityDropoffTimeSetting = {
                facilityId: facilityId,
                startTimeOfDropOffWindow: null,
                endTimeOfDropOffWindow: null,
                timeIntervalInMinutes: null,
                allowance: null,
            };
            this.setFacilityDropoffTimeSettingFormValues(emptyFacilityDropoffTimeSetting);
        } finally {
            this.setOperationCompleted(true);
            this.setLoadingInitial(false);
        }
    };

    saveFacilityDropoffTimeSetting = async (facilityDropoffTimeSetting: FacilityDropoffTimeSetting) => {
        try {
            this.setOperationError('');
            this.setOperationCompleted(false);

            await agent.Facility.saveFacilityDropoffTimeSetting(facilityDropoffTimeSetting);

            this.setFacilityDropoffTimeSetting(facilityDropoffTimeSetting);
        } catch (error) {
            let apiError: IApiError = error as IApiError;

            if (apiError.businessValidationMessage != '') this.setOperationError(apiError.businessValidationMessage);
            else this.setOperationError('Oops! something went wrong. Please try again.');
        } finally {
            this.setOperationCompleted(true);
        }
    };

    getWaitlistStatistics = async (facilityId: string) => {
        try {
            const stats = await agent.Facility.getFacilityWaitlistStats(facilityId);
            console.log(stats);
            this.setWaitlistStats(stats);

            return stats;
        } catch (error) {
            console.error(error);
        }
    };

    setWaitlistStats = (waitlistStats: WaitlistStatics) => {
        this.waitlistStats = waitlistStats;
    };

    getFacilityCapacityMetrics = async (facilityId: string) => {
        try {
            this.setOperationError('');
            this.setOperationCompleted(false);
            this.setLoadingInitial(true);

            let capacityMetrics = await agent.Facility.getFacilityCapacityMetrics(facilityId);

            this.setFacilityCapacityMetrics(capacityMetrics);

            this.setFacilityRepairSeasonSummaryData(this.calculateTotalCapacity(capacityMetrics));

            //debugger
            return capacityMetrics;
        } catch (error) {
            this.setOperationError('Oops! something went wrong. Please try again.');
            console.log(error);
        } finally {
            this.setOperationCompleted(true);
            this.setLoadingInitial(false);
        }
    };

    getFacilityCapacityMetricsOfAWeek = async (
        facilityId: string,
        forTheWeekStartingOn: Date
    ): Promise<FacilityCapacityMetricForAWeek | undefined> => {
        try {
            this.setOperationError('');
            this.setOperationCompleted(false);
            this.setLoadingInitial(true);

            let capacityMetrics = await agent.Facility.getFacilityCapacityMetrics(facilityId, forTheWeekStartingOn);

            let capacityMetricsOfAWeek: FacilityCapacityMetricForAWeek | undefined = undefined;

            if (capacityMetrics.length === 1) capacityMetricsOfAWeek = capacityMetrics[0];

            //this.setFacilityCapacityMetricsOfAWeek(capacityMetrics);

            //debugger
            return capacityMetricsOfAWeek;
        } catch (error) {
            this.setOperationError('Oops! something went wrong. Please try again.');
            console.log(error);
        } finally {
            this.setOperationCompleted(true);
            this.setLoadingInitial(false);
        }
    };

    getWeeksWithoutCapacity = async (facilityId: string): Promise<Date[] | undefined> => {
        try {
            this.setOperationError('');
            this.setOperationCompleted(false);
            this.setLoadingInitial(true);

            let weeksWithoutCapacity = await agent.Facility.getWeeksWithoutCapacity(facilityId);

            //this.setFacilityCapacityMetricsOfAWeek(capacityMetrics);

            //debugger
            return weeksWithoutCapacity;
        } catch (error) {
            this.setOperationError('Oops! something went wrong. Please try again.');
            console.log(error);
        } finally {
            this.setOperationCompleted(true);
            this.setLoadingInitial(false);
        }
    };

    copyCapacity = async (request: FacilityCapacityCopyRequest) => {
        try {
            this.setOperationError('');
            this.setOperationCompleted(false);

            await agent.Facility.copyCapacity(request);
        } catch (error) {
            let apiError: IApiError = error as IApiError;

            if (apiError.businessValidationMessage != '') this.setOperationError(apiError.businessValidationMessage);
            else this.setOperationError('Oops! something went wrong. Please try again.');
        } finally {
            this.setOperationCompleted(true);
        }
    };

    private calculateCapacityTotalsForTheWeek() {
        this.totalCapacityForTheWeek.PDR = this.facilityRepairCapacityFormValues.configEntriesForTheWeek
            .map((x) => x.capacityItems)
            .reduce((total, item) => (total += item.find((x) => x.repairType === 'PDR')?.capacity ?? 0), 0);
        this.totalCapacityForTheWeek.PTP = this.facilityRepairCapacityFormValues.configEntriesForTheWeek
            .map((x) => x.capacityItems)
            .reduce((total, item) => (total += item.find((x) => x.repairType === 'PTP')?.capacity ?? 0), 0);
        this.totalCapacityForTheWeek.CON = this.facilityRepairCapacityFormValues.configEntriesForTheWeek
            .map((x) => x.capacityItems)
            .reduce((total, item) => (total += item.find((x) => x.repairType === 'CON')?.capacity ?? 0), 0);
    }

    calculateTotalCapacity(data: FacilityCapacityMetricForAWeek[]): FacilityRepairSeasonSummaryData {
        let summaryData: FacilityRepairSeasonSummaryData = {
            PDR: 0,
            PTP: 0,
            CON: 0,
            grandTotal: 0,
            numberOfClosedDays: 0,
        };

        summaryData.PDR = data
            .map((x) => x.capacityOfWeek)
            .reduce((total, item) => (total += item.find((x) => x.repairType === 'PDR')?.capacity ?? 0), 0);
        summaryData.PTP = data
            .map((x) => x.capacityOfWeek)
            .reduce((total, item) => (total += item.find((x) => x.repairType === 'PTP')?.capacity ?? 0), 0);
        summaryData.CON = data
            .map((x) => x.capacityOfWeek)
            .reduce((total, item) => (total += item.find((x) => x.repairType === 'CON')?.capacity ?? 0), 0);
        summaryData.grandTotal = summaryData.PDR + summaryData.PTP + summaryData.CON;
        summaryData.numberOfClosedDays = data
            .map((x) => x.closedDatesInTheWeek.length)
            .reduce((total, numberOfClosedDays) => total + numberOfClosedDays);

        return summaryData;
    }
}
